import { useEffect, useState } from 'react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { RequestWithRelationsFromRequestsList, useBulkUpdateSupervisor } from '@/models/request';
import { useUsers } from '@/models/user';
import { getAssignSkipCounts } from '@/routes/Requests/components/BulkActionBar/getBulkEligibility';
import useDebouncedState from '@/utils/useDebouncedState';

import { BulkConfirmationDialog } from './BulkConfirmationDialog';

const NO_SUPERVISOR_ID = '__none__';

type SupervisorOption = {
  id: string;
  name: string;
};

export const BulkAssignDialog = ({
  showDialog,
  setShowDialog,
  selectedRequests,
  eligibleRequests,
  onSuccess,
}: {
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  selectedRequests: RequestWithRelationsFromRequestsList[];
  eligibleRequests: RequestWithRelationsFromRequestsList[];
  onSuccess: () => void;
}) => {
  const { t } = useLingui();
  const [selectedOption, setSelectedOption] = useState<SupervisorOption | null>(null);
  const [search, debouncedSearch, setSearch] = useDebouncedState('');

  const { data: { users } = { users: [] }, isFetching: isFetchingUsers } = useUsers(
    {
      permissions: ['supervise_request'],
      search: debouncedSearch,
    },
    {
      enabled: showDialog,
      keepPreviousData: true,
    }
  );

  const { mutateAsync: updateSupervisors, isPending } = useBulkUpdateSupervisor();

  useEffect(() => {
    if (showDialog) {
      setSelectedOption(null);
      setSearch('');
    }
  }, [setSearch, showDialog]);

  const noSupervisorLabel = t({
    id: 'requests.bulk.assign.picker.empty',
    message: 'No supervisor',
  });
  const selectSupervisorLabel = t({
    id: 'requests.bulk.assign.picker.label',
    message: 'Select a supervisor',
  });

  const noSupervisorOption: SupervisorOption = { id: NO_SUPERVISOR_ID, name: noSupervisorLabel };
  const userOptions: SupervisorOption[] = users.map((user) => ({ id: user.id, name: user.name }));
  const options = [noSupervisorOption, ...userOptions];

  // undefined: nothing chosen yet, null: remove the supervisor
  const supervisorId = selectedOption?.id === NO_SUPERVISOR_ID ? null : selectedOption?.id;
  const isRemoval = supervisorId === null;
  const targetRequests = isRemoval
    ? eligibleRequests.filter((request) => !!request.supervisor)
    : eligibleRequests;
  const targetCount = targetRequests.length;
  const skipCounts = getAssignSkipCounts(selectedRequests, eligibleRequests, isRemoval);
  const hasChosenSupervisor = supervisorId !== undefined;

  return (
    <BulkConfirmationDialog
      title={<Trans id="requests.bulk.assign.title">Assign a supervisor</Trans>}
      confirm={
        isRemoval ? (
          <Trans id="requests.bulk.assign.confirm.remove">Remove</Trans>
        ) : (
          <Trans id="requests.bulk.assign.confirm">Assign</Trans>
        )
      }
      confirmButtonVariant={isRemoval ? 'danger' : 'primary'}
      isConfirmDisabled={supervisorId === undefined || targetCount === 0}
      showDialog={showDialog}
      setShowDialog={setShowDialog}
      isPending={isPending}
      requests={selectedRequests}
      onCloseAfterResult={onSuccess}
      onConfirm={async () => {
        if (supervisorId === undefined || targetCount === 0) {
          return { succeeded: [], failed: [] };
        }

        return updateSupervisors({
          requestIds: targetRequests.map((request) => request.id),
          supervisorId,
        });
      }}
    >
      <Stack gap="1rem">
        <InputSelect
          variant="select"
          aria-label={selectSupervisorLabel}
          placeholder={selectSupervisorLabel}
          options={options}
          value={selectedOption}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
          inputValue={search}
          onInputChange={setSearch}
          filterOption={null}
          isLoading={isFetchingUsers}
          isSearchable
          onChange={(option) => setSelectedOption(option ?? null)}
        />
        {hasChosenSupervisor && (
          <Stack gap="0.5rem">
            <Paragraph>
              {isRemoval ? (
                <Plural
                  id="requests.bulk.assign.scope.remove"
                  value={targetCount}
                  one="# request will have no supervisor"
                  other="# requests will have no supervisor"
                />
              ) : (
                <Plural
                  id="requests.bulk.assign.scope"
                  value={targetCount}
                  one="# request will be assigned"
                  other="# requests will be assigned"
                />
              )}
            </Paragraph>
            {skipCounts.draft > 0 && (
              <Message type="neutral">
                <Plural
                  id="requests.bulk.assign.skip.draft"
                  value={skipCounts.draft}
                  one="# draft request won't be assigned"
                  other="# draft requests won't be assigned"
                />
              </Message>
            )}
            {skipCounts.forbidden > 0 && (
              <Message type="neutral">
                <Plural
                  id="requests.bulk.assign.skip.forbidden"
                  value={skipCounts.forbidden}
                  one="# request you don't have permission to assign"
                  other="# requests you don't have permission to assign"
                />
              </Message>
            )}
            {skipCounts.alreadyUnassigned > 0 && (
              <Message type="neutral">
                <Plural
                  id="requests.bulk.assign.skip.already-unassigned"
                  value={skipCounts.alreadyUnassigned}
                  one="# request already has no supervisor"
                  other="# requests already have no supervisor"
                />
              </Message>
            )}
          </Stack>
        )}
      </Stack>
    </BulkConfirmationDialog>
  );
};
